// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPreviousPurchase from "@plumberssupply/App/Components/Product/ProductPreviousPurchase";
import ProductScratchAndDentBadge, {
    ScratchAndDentStyles,
} from "@plumberssupply/App/Components/Product/ProductScratchAndDentBadge";
import RoundToBox from "@plumberssupply/App/Components/Product/RoundToBox";
import { RoundToBoxStyles } from "@plumberssupply/App/Components/Product/RoundToBox";
import { getRoundToBox, getShowRoundToBox } from "@plumberssupply/App/Store/Selectors/CustomProductDetailsSelectors";
import ProductAddToCartButton from "@plumberssupply/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@plumberssupply/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@plumberssupply/Overrides/Components/ProductUnitOfMeasureSelect";
import { mergeWithCss, xIsAuthenticated, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    widgetId: string;
    product: ProductModel;
    extendedStyles?: ShopByCategoryItemStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
    };
};

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ShopByCategoryItemStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    actionSection?: InjectableCss;
    addToCartSection?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceStyles?: ProductPriceStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productPreviousPurchase?: TypographyPresentationProps;
    productImageWrapper?: InjectableCss;
    scratchAndDentBadge?: ScratchAndDentStyles;
    scratchAndDentBadgeWrapper?: InjectableCss;
    priceRtbWrapper?: InjectableCss;
    roundToBox?: RoundToBoxStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
}

export const shopByCategoryItem: ShopByCategoryItemStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            height: 100%;
            padding-right: 12px;

            ${xScreen("lte", "sm")} {
                padding-right: 0;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 80px;
                height: 80px;
                margin-right: 20px;
                margin-bottom: 12px;
                flex: 0 0 100px;
            `,
        },
    },
    infoSection: {
        css: css`
            max-width: 274px;
            display: flex;
            flex-direction: column;
            flex: 1;
            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 8px;
                min-height: 36px;
                align-items: flex-start;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            gap: 4,
            css: css`
                margin-bottom: 8px;
            `,
        },
    },
    productPriceStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                margin: 8px 16px 8px 0;
            `,
        },
    },
    actionSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 100%;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-top: auto;
        `,
    },
    addToCartSection: {
        css: css`
            display: flex;
            align-items: center;
            flex-basis: 100%;
            > div:first-child {
                margin-right: 11px;
            }
            ${xScreen("lt", 1200)} {
                flex-wrap: wrap;
                row-gap: 8px;
            }
        `,
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        css: css`
            width: 180px;
            margin-right: 32px;
            ${xScreen("lt", "lg")} {
                margin-right: 20px;
                min-width: 146px;
                width: 146px;
            }
        `,
    },
    productAddToListLinkStyles: {
        link: {
            css: css`
                flex-shrink: 0;
                margin: 0;
                ${xScreen("lte", "xs")} {
                    margin-left: auto;
                }
                ${xScreen("lte", 382)} {
                    margin-left: 0;
                }
            `,
        },
    },
    productImageWrapper: {
        css: css`
            padding-bottom: 5px;
        `,
    },
    productPreviousPurchase: {
        css: css`
            line-height: 18px;
            padding: 3px 8px;
        `,
    },
    scratchAndDentBadgeWrapper: {
        css: css`
            height: 30px;
        `,
    },
    scratchAndDentBadge: {
        wrapper: {
            css: css`
                margin-top: 0;
                margin-bottom: 12px;
            `,
        },
    },
    priceRtbWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            row-gap: 5px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin: 7px 10px 0 -7px;
            `,
            inputSelect: css`
                padding-left: 8px;
            `,
        },
    },
};

const ShopByCategoryItem: React.FC<Props> = ({ product, updateProductInfo, productInfo, extendedStyles, widgetId }) => {
    const [styles] = useState(() => mergeWithCss(shopByCategoryItem, extendedStyles));
    const { settingsCollection, isAuthenticated } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        settingsCollection: getSettingsCollection(state),
    }));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const [roundToBox, setRoundToBox] = useState(getRoundToBox(product?.properties));

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.productImageWrapper}>
                    <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    <ProductPreviousPurchase product={product} extendedStyles={styles.productPreviousPurchase} />
                </StyledWrapper>
                <StyledWrapper {...styles.infoSection} data-test-selector="ShopByCategory-InfoSection">
                    {productContext.product?.brand ? (
                        <ProductBrand brand={productContext.product.brand} extendedStyles={styles.productBrandStyles} />
                    ) : (
                        <StyledWrapper css={styles.productBrandStyles?.nameLink?.css} />
                    )}
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                    {settingsCollection.productSettings.showInventoryAvailability && (
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    )}
                    <StyledWrapper {...styles.priceRtbWrapper}>
                        <ProductPrice
                            product={productContext}
                            extendedStyles={styles.productPriceStyles}
                            showLabel={false}
                            showUnitOfMeasure={
                                productContext.product && productContext.product.unitOfMeasures?.length === 1
                            }
                        />
                        {productContext?.product.unitOfMeasures &&
                            productContext?.product.unitOfMeasures.length > 1 && (
                                <ProductUnitOfMeasureSelect
                                    labelOverride=""
                                    extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                    id={`${productContext?.product.id}-uom`}
                                />
                            )}
                        {getShowRoundToBox(product.properties) && (
                            <RoundToBox
                                isChecked={roundToBox}
                                sizeVariant="small"
                                toggleHandler={setRoundToBox}
                                extendedStyles={styles.roundToBox}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.scratchAndDentBadgeWrapper}>
                        <ProductScratchAndDentBadge extendedStyles={styles.scratchAndDentBadge} />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.actionSection}>
                    <StyledWrapper {...styles.addToCartSection}>
                        <ProductQuantityOrdered
                            labelOverride=""
                            showActions={true}
                            extendedStyles={styles.productQuantityOrderedStyles}
                            id={`${productContext.product.id}-qty`}
                        />
                        <ProductAddToCartButton
                            data-test-selector={`${productContext.product.id}-addToCartBtn`}
                            isRoundToBox={roundToBox}
                            extendedStyles={styles.productAddToCartButtonStyles}
                        />
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                            {...{ isRoundToBox: roundToBox }}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ShopByCategoryItem);
